/** Bulk menu selection stages a session-persisted cart, then opens school checkout.
 * Billing, insufficient funds, repricing consent and order submission live there.
 */
import { supplyCaseShortfalls } from '@ordo/schema/src/managers/supply-minimum'
import { Alert, AlertDescription } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { LoadingButton } from '@ordo/ui/loading-button'
import { Skeleton } from '@ordo/ui/skeleton'
import { Textarea } from '@ordo/ui/textarea'
import { type ReactElement, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import type { AdminSchool } from '@/api/school-orders'
import { useSupplyCatalogQuery, useSupplyOrdersQuery } from '@/api/supplies'
import { QuantityTile } from '@/components/orders/shared/quantity-tile'
import {
  bulkCatalogForScope,
  type BulkCatalogScope,
  EMPTY_SUPPLY_CART,
  formatSupplyCents,
  MAX_SUPPLY_LINE_QUANTITY,
  type SupplyCart,
  supplyCartAdd,
  supplyCartDecrement,
  supplyCartLines,
  supplyCartShipLabel,
  supplyCartSubtotalCents,
} from '@/lib/school-orders/supplies-cart'
import { serviceCheckoutUrl, useSchoolServiceCart } from '@/stores/school-service-cart'

export function SuppliesTab({
  school,
  scope = 'all',
}: {
  school: AdminSchool
  scope?: BulkCatalogScope
}): ReactElement {
  const navigate = useNavigate()
  const put = useSchoolServiceCart((state) => state.put)
  const catalog = useSupplyCatalogQuery(school.id)
  const orders = useSupplyOrdersQuery(school.id)
  const [cart, setCart] = useState<SupplyCart>(EMPTY_SUPPLY_CART)
  const [notes, setNotes] = useState('')
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const scopedCatalog = useMemo(() => bulkCatalogForScope(catalog.data?.lines ?? [], scope), [catalog.data, scope])
  const lines = useMemo(() => supplyCartLines(cart, scopedCatalog), [cart, scopedCatalog])
  const shortfalls = supplyCaseShortfalls(lines.map((line) => ({ ...line.item, quantity: line.quantity })))
  const subtotal = supplyCartSubtotalCents(lines)
  const shipLabel = supplyCartShipLabel(lines)

  const addToCart = (): void => {
    if (!lines.length || shortfalls.length) {
      return
    }
    put({
      id: idempotencyKey,
      kind: 'bulk',
      locationId: school.id,
      locationName: school.name,
      title: scope === 'backup' ? 'Shelf-stable backup meals' : 'Bulk supplies',
      supplyLines: lines,
      notes,
      lines: lines.map((line) => ({
        name: line.item.name,
        quantity: line.quantity,
        unitPriceCents: line.item.customerPriceCents,
        detail: `${line.item.unitLabel} · ${line.item.shipLabel}`,
      })),
    })
    navigate(serviceCheckoutUrl(school.id))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto sm:flex-row sm:overflow-visible">
      {/* ── Catalog (left) ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 p-6 sm:min-h-0 sm:flex-1">
        <p className="text-sm text-muted-foreground">
          {scope === 'backup'
            ? 'Shelf-stable backup meals ship by the case. No service date is needed. Orders bill the school account.'
            : 'Bulk supplies ship to the school and bill the school account when you place the order.'}
        </p>
        <div className="overflow-x-hidden sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
          {catalog.isError ? (
            <Alert variant="error">
              <AlertDescription>Could not load the supplies catalog. Close and try again.</AlertDescription>
            </Alert>
          ) : catalog.isPending ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="aspect-square rounded-2xl" />
              ))}
            </div>
          ) : scopedCatalog.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-6 text-center text-sm text-muted-foreground">
              {scope === 'backup'
                ? 'No shelf-stable backup meals are available for this campus yet.'
                : 'Nothing in the supplies catalog yet.'}
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {scopedCatalog.map((line) => (
                <section key={line.lineCode}>
                  <h3 className="mb-1 text-sm font-semibold text-muted-foreground">{line.label}</h3>
                  <p className="mb-3 text-xs text-muted-foreground">{line.description}</p>
                  {[
                    ...new Map(
                      line.items.flatMap((item) =>
                        (item.casePolicies ?? []).map((policy) => [policy.menuId, policy] as const),
                      ),
                    ).values(),
                  ].map((policy) => (
                    <Badge key={policy.menuId} variant="warning" className="mb-3">
                      Minimum {policy.minimumCases} cases total · Mix and match
                    </Badge>
                  ))}
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4">
                    {line.items.map((item) => (
                      <QuantityTile
                        key={item.supplyItemId}
                        name={item.name}
                        imageUrl={item.imageUrl}
                        imageFit="contain"
                        qty={cart[item.supplyItemId] ?? 0}
                        max={MAX_SUPPLY_LINE_QUANTITY}
                        disabled={catalog.isFetching}
                        caption={
                          <>
                            <span className="text-foreground">{formatSupplyCents(item.customerPriceCents)}</span>
                            {` · ${item.unitLabel}`}
                            {item.allergens?.length ? (
                              <p className="mt-2">Contains {item.allergens.join(', ')}</p>
                            ) : null}
                            <div className="mt-2">
                              <Badge variant="info" className="max-w-full whitespace-normal text-left leading-relaxed">
                                {item.shipLabel}
                              </Badge>
                            </div>
                          </>
                        }
                        onAdd={() => setCart((c) => supplyCartAdd(c, item.supplyItemId))}
                        onDecrement={() => setCart((c) => supplyCartDecrement(c, item.supplyItemId))}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Cart (right) ───────────────────────────────────────────── */}
      <div className="flex flex-col border-t sm:w-[400px] sm:shrink-0 sm:border-t-0 sm:border-l">
        <div className="flex flex-1 flex-col gap-4 p-6">
          <h3 className="text-2xl font-semibold">Cart</h3>
          {lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">Tap an item to add it.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {lines.map((line) => (
                <li key={line.item.supplyItemId} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">
                    {line.item.name} <span className="text-muted-foreground">× {line.quantity}</span>
                  </span>
                  <span className="tabular-nums">{formatSupplyCents(line.totalCents)}</span>
                </li>
              ))}
            </ul>
          )}
          <Textarea
            aria-label="Delivery note"
            placeholder="Delivery note (optional) — dock, hours, contact"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          <RecentSupplyOrders
            orders={(orders.data?.orders ?? []).filter(
              (order) =>
                scope === 'all' ||
                order.lines.some((line) =>
                  scope === 'backup' ? line.lineCode === 'SHELF_STABLE_MEALS' : line.lineCode !== 'SHELF_STABLE_MEALS',
                ),
            )}
          />
        </div>
        <div className="border-t p-6">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-muted-foreground">Bills to school</span>
            <span className="text-2xl font-semibold tabular-nums">{formatSupplyCents(subtotal)}</span>
          </div>
          {shipLabel ? <p className="mt-1 text-sm text-muted-foreground">{shipLabel}</p> : null}
          {shortfalls.map((value) => (
            <p key={value.menuId} role="alert" className="mt-3 text-sm text-destructive">
              {value.message}
            </p>
          ))}
          <LoadingButton
            type="button"
            className="mt-4 h-11 w-full rounded-full text-base"
            disabled={lines.length === 0 || catalog.isError || shortfalls.length > 0}
            onClick={addToCart}>
            Add to cart
          </LoadingButton>
        </div>
      </div>
    </div>
  )
}

function RecentSupplyOrders({
  orders,
}: {
  orders: ReadonlyArray<{ id: string; statusLabel: string; shipLabel: string; totalCents: number; createdAt: string }>
}): ReactElement | null {
  if (orders.length === 0) {
    return null
  }
  return (
    <div className="mt-2">
      <span className="text-sm font-semibold text-muted-foreground">Recent orders</span>
      <ul className="mt-2 flex flex-col gap-1.5">
        {orders.slice(0, 3).map((order) => (
          <li key={order.id} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-muted-foreground">
              {new Date(order.createdAt).toLocaleDateString()} · {order.statusLabel}
            </span>
            <span className="tabular-nums">{formatSupplyCents(order.totalCents)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
